import {
  Trees,
  Shapes,
  Box,
  Baby,
  Building2,
  Gem,
  Briefcase,
  BrickWall,
} from "lucide-react";

/**
 * Mural collection themes — used on the landing page gallery
 * and the individual collection browse pages.
 */
export const MURAL_COLLECTIONS = [
  {
    slug: "nature-landscape",
    title: "Nature & Landscape",
    short: "Forests, mountains, lakes, and open skies",
    description:
      "Bring the outdoors in with misty forests, panoramic mountain ranges, and serene lakeside views. Perfect for living rooms, bedrooms, and spa-like interiors.",
    icon: Trees,
    image:
      "https://images.pexels.com/photos/36720215/pexels-photo-36720215.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "nature",
      "forest",
      "woodland",
      "lake",
      "landscape",
      "misty",
      "mountain",
      "panoramic",
      "calming",
      "tree",
      "trees",
      "enchanted",
      "sky",
      "sunset",
      "sunrise",
    ],
  },
  {
    slug: "abstract-texture",
    title: "Abstract & Texture",
    short: "Geometric, fluid, and artistic patterns",
    description:
      "Bold geometric forms, fluid colour washes, and textured surfaces that add depth without overpowering. Ideal for modern living spaces and feature walls.",
    icon: Shapes,
    image:
      "https://images.pexels.com/photos/11665680/pexels-photo-11665680.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "abstract",
      "futuristic",
      "geometric",
      "modern",
      "texture",
      "fluid",
      "artistic",
      "pattern",
      "colour",
      "color",
      "glowing",
    ],
  },
  {
    slug: "3d-illusion",
    title: "3D Illusion",
    short: "Optical depth, breakthrough effects, trompe-l'oeil",
    description:
      "Eye-catching murals that play with perspective. From breakthrough effects to impossible geometry — these designs make walls disappear.",
    icon: Box,
    image:
      "https://images.pexels.com/photos/14404214/pexels-photo-14404214.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "3d",
      "3D",
      "breakthrough",
      "optical",
      "illusion",
      "trompe",
      "perspective",
      "depth",
      "tiger",
    ],
  },
  {
    slug: "kids-nursery",
    title: "Kids & Nursery",
    short: "Playful, imaginative, age-appropriate designs",
    description:
      "Space adventures, woodland creatures, and gentle pastel worlds. Designed to inspire little imaginations in bedrooms, playrooms, and nurseries.",
    icon: Baby,
    image:
      "https://images.pexels.com/photos/6835137/pexels-photo-6835137.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "kids",
      "nursery",
      "space",
      "adventure",
      "cartoon",
      "children",
      "playful",
      "playroom",
      "baby",
    ],
  },
  {
    slug: "urban-architecture",
    title: "Urban & Architecture",
    short: "Cityscapes, skylines, and street scenes",
    description:
      "Skyline panoramas, moody street scenes, and architectural photography. Suited to offices, loft conversions, and contemporary living.",
    icon: Building2,
    image:
      "https://images.pexels.com/photos/6899348/pexels-photo-6899348.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "urban",
      "cityscape",
      "city",
      "architecture",
      "skyline",
      "street",
      "building",
      "loft",
      "godzilla",
      "kong",
    ],
  },
  {
    slug: "luxury-interiors",
    title: "Luxury Interiors",
    short: "Botanical, tropical, and high-end finishes",
    description:
      "Tropical palms, botanical prints, marble veins, and gold-leaf textures. For rooms that demand presence.",
    icon: Gem,
    image:
      "https://images.unsplash.com/photo-1759262152168-ba49180afcb4?w=800&q=80",
    keywords: [
      "luxury",
      "tropical",
      "botanical",
      "premium",
      "elegant",
      "marble",
      "gold",
      "parrot",
      "jungle",
      "palm",
    ],
  },
  {
    slug: "office-commercial",
    title: "Office & Commercial",
    short: "Branded, motivational, and professional environments",
    description:
      "Transform workspaces, reception areas, and meeting rooms. From calm abstract to bold branded graphics.",
    icon: Briefcase,
    image:
      "https://images.unsplash.com/photo-1765366417031-60bc8543189c?w=800&q=80",
    keywords: [
      "office",
      "commercial",
      "corporate",
      "professional",
      "workspace",
      "reception",
      "meeting",
    ],
  },
  {
    slug: "brick-concrete-industrial",
    title: "Brick, Concrete & Industrial",
    short: "Raw textures, exposed finishes, loft style",
    description:
      "Achieve the industrial look without the structural work. Exposed brick, raw concrete, weathered metal, and distressed plaster effects.",
    icon: BrickWall,
    image:
      "https://images.pexels.com/photos/35876780/pexels-photo-35876780.jpeg?auto=compress&cs=tinysrgb&w=800",
    keywords: [
      "brick",
      "concrete",
      "industrial",
      "stone",
      "grunge",
      "loft",
      "raw",
      "weathered",
      "exposed",
      "distressed",
    ],
  },
];

/**
 * Match a product to a collection by checking tags + title against keywords.
 * Returns the first matching collection slug, or null.
 */
export function classifyMural(product) {
  const tags = (product.tags || []).map((t) => t.toLowerCase());
  const title = (product.title || "").toLowerCase();
  const combined = [...tags, ...title.split(/\s+/)];

  for (const col of MURAL_COLLECTIONS) {
    if (col.keywords.some((kw) => combined.some((w) => w.includes(kw)))) {
      return col.slug;
    }
  }
  return null;
}

/**
 * Get all matching collections for a product (a product can appear in multiple).
 */
export function classifyMuralAll(product) {
  const tags = (product.tags || []).map((t) => t.toLowerCase());
  const title = (product.title || "").toLowerCase();
  const combined = [...tags, ...title.split(/\s+/)];

  return MURAL_COLLECTIONS.filter((col) =>
    col.keywords.some((kw) => combined.some((w) => w.includes(kw)))
  ).map((col) => col.slug);
}
